import React, { useEffect, useState } from 'react';
import DatePicker from "react-datepicker";
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import {
    Button,
    Col,
    Form,
    Row,
    Spinner,
} from 'react-bootstrap';
import { ArrowLeft } from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import OutletSelect from '../../components/OutletSelect';
import { he } from 'date-fns/locale';

/* ─── inline styles ─────────────────────────────────────────────── */
const S = {
    page: {
        background: '#f3f6f8',
        minHeight: '100vh',
        padding: '24px',
        // fontFamily: 'Arial, Helvetica, sans-serif',
    },
    /* top filter card */
    filterCard: {
        background: '#ffffff',
        borderRadius: 16,
        padding: '24px 28px',
        marginBottom: 24,
        boxShadow: '0 1px 4px rgba(0,0,0,.06)',
    },
    filterHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    filterTitle: {
        margin: 0,
        fontSize: 20,
        fontWeight: 700,
        color: '#202124',
    },
    /* hero / brand bar */
    heroBand: {
        background: '#09A5F7',
        borderRadius: '20px 20px 0 0',
        padding: '0',
        overflow: 'hidden',
    },
    heroBandImg: {
        width: '100%',
        display: 'block',
        maxHeight: 130,
        // height: '100%',
        objectFit: 'cover',
    },
    /* main content card */
    mainCard: {
        background: '#f7f7f8',
        // borderRadius: '0 0 20px 20px',
        padding: '32px 32px',
        boxShadow: '0 4px 24px rgba(0,0,0,.08)',
    },
    /* report header inside card */
    reportHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        marginBottom: 24,
    },
    reportIcon: {
        width: 56,
        height: 56,
        lineHeight: '56px',
        textAlign: 'center',
        background: '#09A5F7',
        borderRadius: '50%',
        fontSize: 28,
        color: '#fff',
        flexShrink: 0,
    },
    reportTitle: {
        margin: 0,
        fontSize: 26,
        fontWeight: 700,
        color: '#202124',
    },
    reportSubtitle: {
        margin: '6px 0 0',
        fontSize: 14,
        color: '#5f6368',
    },
    /* meta row */
    metaBox: {
        background: '#eaf1f7',
        borderRadius: 14,
        display: 'flex',
        marginBottom: 24,
        overflow: 'hidden',
    },
    metaCell: {
        flex: 1,
        padding: '22px 26px',
        borderRight: '1px solid #c7d7e6',
    },
    metaCellLast: {
        flex: 1,
        padding: '22px 26px',
    },
    metaLabel: { fontSize: 13, color: '#6b7280', marginBottom: 6 },
    metaValue: { fontSize: 18, fontWeight: 700, color: '#111827' },
    /* KPI cards */
    kpiGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 24,
    },
    kpiCard: (border) => ({
        background: '#fff',
        border: `1px solid ${border}`,
        borderRadius: 16,
        padding: 18,
    }),
    kpiCardTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    kpiEmoji: { fontSize: 24 },
    kpiBadge: (bg, color) => ({
        background: bg,
        color,
        fontSize: 11,
        fontWeight: 700,
        padding: '5px 8px',
        borderRadius: 20,
    }),
    kpiLabel: { fontSize: 13, color: '#5f6368', marginTop: 14 },
    kpiValue: { fontSize: 26, fontWeight: 800, color: '#111827', marginTop: 4, lineHeight: 1.2 },
    kpiSub: { fontSize: 12, color: '#6b7280', marginTop: 5 },
    kpiDivider: { height: 1, background: '#edf2f7', margin: '14px 0 10px' },
    kpiDetailRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 12,
        color: '#6b7280',
        marginTop: 6,
    },
    /* two-col panels */
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 },
    panel: {
        background: '#fff',
        border: '1px solid #d9dee5',
        borderRadius: 14,
        padding: 18,
    },
    panelTitle: { fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 14 },
    panelRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 14,
        color: '#374151',
        padding: '8px 0',
        borderTop: '1px solid #eef2f7',
    },
    panelRowFirst: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 14,
        color: '#374151',
        padding: '8px 0',
    },
    /* metrics table */
    tableWrap: {
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid #d9dee5',
        marginBottom: 24,
    },
    tableHead: {
        display: 'grid',
        gridTemplateColumns: '60px 1fr 200px',
        background: '#09A5F7',
        padding: '13px 0',
    },
    thCell: (align) => ({
        fontSize: 14,
        color: '#fff',
        fontWeight: 700,
        padding: '0 16px',
        textAlign: align || 'left',
    }),
    tableRow: (bg) => ({
        display: 'grid',
        gridTemplateColumns: '60px 1fr 200px',
        borderTop: '1px solid #e5e7eb',
        background: bg || '#fff',
    }),
    tdNum: {
        fontSize: 14,
        color: '#6b7280',
        padding: '14px 12px',
        textAlign: 'center',
    },
    tdLabel: (bold) => ({
        fontSize: 14,
        color: '#202124',
        padding: '14px 16px',
        fontWeight: bold ? 700 : 400,
    }),
    tdVal: (color, bold, size) => ({
        fontSize: size || 14,
        color: color || '#202124',
        padding: '14px 16px',
        textAlign: 'right',
        fontWeight: bold ? 700 : 400,
    }),
    /* revenue flow note */
    noteBox: {
        background: '#eef6ff',
        border: '1px solid #d6e8ff',
        borderRadius: 14,
        padding: '14px 18px',
        fontSize: 13,
        lineHeight: '22px',
        color: '#475467',
        marginBottom: 32,
    },
    /* footer */
    footer: {
        background: '#09A5F7',
        borderRadius: '0 0 20px 20px',
        padding: '22px 20px 26px',
        textAlign: 'center',
        color: '#fff',
        fontSize: 13,
        lineHeight: '22px',
    },
    /* empty / loading states */
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 0',
        color: '#9ca3af',
    },
};

/* ─── helpers ───────────────────────────────────────────────────── */
const fmt = (n, fallback = '0.00') =>
    n != null && !isNaN(n) ? Number(n).toFixed(2) : fallback;

const fmtINR = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

/* ─── sub-components ─────────────────────────────────────────────── */
const KpiCard = ({ emoji, badge, badgeBg, badgeColor, borderColor, label, value, sub, detail1L, detail1V, detail1Color, detail2L, detail2V, detail2Color }) => (
    <div style={S.kpiCard(borderColor)}>
        <div style={S.kpiCardTop}>
            <span style={S.kpiEmoji}>{emoji}</span>
            <span style={S.kpiBadge(badgeBg, badgeColor)}>{badge}</span>
        </div>
        <div style={S.kpiLabel}>{label}</div>
        <div style={S.kpiValue}>{value}</div>
        <div style={S.kpiSub}>{sub}</div>
        <div style={S.kpiDivider} />
        <div style={S.kpiDetailRow}>
            <span>{detail1L}</span>
            <span style={{ fontWeight: 700, color: detail1Color || '#111827' }}>{detail1V}</span>
        </div>
        <div style={S.kpiDetailRow}>
            <span>{detail2L}</span>
            <span style={{ fontWeight: 700, color: detail2Color || '#111827' }}>{detail2V}</span>
        </div>
    </div>
);

const MetricRow = ({ idx, label, value, bg, labelBold, valColor, valBold, valSize, note }) => (
    <div style={S.tableRow(bg)}>
        <div style={S.tdNum}>{idx}</div>
        <div style={S.tdLabel(labelBold)}>
            {label}
            {note && <span style={{ color: '#2563eb', fontWeight: 400, fontSize: 12, marginLeft: 6 }}>{note}</span>}
        </div>
        <div style={S.tdVal(valColor, valBold, valSize)}>{value}</div>
    </div>
);

/* ═══════════════════════════════════════════════════════════════════ */
const DaySummaryReport = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();

    const accessToken = localStorage.getItem('authToken');
    const [subBtnLoading, setSubBtnLoading] = useState(false);
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [selectedOutletName, setSelectedOutletName] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [CouponCodeOrders, setCouponCodeOrders] = useState([]);
    const [totalOrdersSummary, setTotalOrdersSummary] = useState({});
    const [userInfo, setuserInfo] = useState({});
    const [clicked, setClicked] = useState(false);

    useEffect(() => {
        const now = new Date();
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        yesterday.setHours(23, 59, 59, 999);
        setStartDate(yesterday);
    }, []);

    useEffect(() => {
        if (selectedOutlet?.label) setSelectedOutletName(selectedOutlet.label);
    }, [selectedOutlet]);

    const getSummary = async (date, outlet) => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const updatedStartDate = date ? date.toISOString().split('T')[0] : null;
            const response = await axios.post('order/summary', { date: updatedStartDate, outlet }, { headers });

            setTotalOrdersSummary(response?.data?.data?.totalOrdersSummary || {});
            setCouponCodeOrders(response?.data?.data?.couponCodeOrders || []);
            setuserInfo(response?.data?.data?.userInfo || {});

            if (response?.data?.status === true) {
                toast.success('Data Fetched Successfully', { position: 'top-right', autoClose: 3000, theme: 'light' });
            } else {
                toast.info('No records available. Please try again later.', { position: 'top-right', autoClose: 3000, theme: 'light' });
            }
        } catch (error) {
            const status = error?.response?.status;
            const msg =
                status === 406 ? error.response?.data?.error?.message || error.response?.data?.error
                    : status === 412 ? error.response?.data?.data?.message
                        : status === 500 ? error.response?.data?.error
                            : 'Something went wrong';
            toast.error(msg, { position: 'top-right', autoClose: 3000, theme: 'light' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedOutlet) return;
        setSubBtnLoading(true);
        setClicked(true);
        const adj = new Date(startDate.setHours(23, 59, 59, 999));
        await getSummary(adj, selectedOutlet.value);
        setSubBtnLoading(false);
    };

    /* ── derived values ── */
    const tos = totalOrdersSummary;
    const netRevenue = (tos.totalRevenue || 0) - (tos.totalCancelledOrdersRevenue || 0);
    const grandTotal = netRevenue + (tos.totaltotalTaxAmount || 0);
    const avgOrderValue = tos.totalOrdersCount ? (netRevenue / tos.totalOrdersCount) : 0;
    const appOrders = (tos.totalOrdersCount || 0) - (tos.totalKioskOrdersCount || 0);
    const appPct = tos.totalOrdersCount ? Math.round((appOrders / tos.totalOrdersCount) * 100) : 0;
    const kioskPct = 100 - appPct;

    const formattedDate = startDate?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`} style={{ ...S.page, background: skin === 'dark' ? '#1a1a2e' : '#f3f6f8' }}>

                {/* ── Filter Bar ── */}
                <div style={S.filterCard}>
                    <div style={S.filterHeader}>
                        <h4 style={S.filterTitle}>
                            <i className="bi bi-calendar-day me-2" />
                            DAY SUMMARY REPORT — {formattedDate}
                        </h4>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => navigate('/reports/all-reports')}
                            className="d-flex align-items-center fw-semibold"
                            style={{ height: 38 }}
                        >
                            <ArrowLeft className="me-1" /> Back to Reports
                        </Button>
                    </div>

                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: '16px 20px' }}>
                        <Row className="g-3 align-items-end">
                            <Col md={4}>
                                <Form.Group>
                                    <OutletSelect
                                        value={selectedOutlet}
                                        onChange={(opt) => setSelectedOutlet(opt)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
                                        <i className="bi bi-calendar me-1" />Date
                                    </Form.Label>
                                    <DatePicker
                                        selected={startDate}
                                        onChange={(d) => setStartDate(d)}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="Select Date"
                                        className="form-control border-secondary"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Button
                                    variant="primary"
                                    onClick={handleSubmit}
                                    className="w-100"
                                    disabled={!selectedOutlet || subBtnLoading}
                                >
                                    {subBtnLoading ? <Spinner size="sm" animation="border" /> : 'Generate Report'}
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* ── Report Body ── */}
                {loading ? (
                    <div style={S.emptyState}>
                        <Spinner animation="border" variant="primary" />
                        <span style={{ marginTop: 12 }}>Loading report data…</span>
                    </div>
                ) : !clicked ? (
                    <div style={S.emptyState}>
                        <i className="bi bi-clipboard-data" style={{ fontSize: 48 }} />
                        <h5 style={{ marginTop: 16, color: '#6b7280' }}>
                            Please select an outlet and date to view day summary
                        </h5>
                    </div>
                ) : (
                    <div>
                        {/* ── Hero band ── */}
                        <div style={S.heroBand}>
                            <img
                                src="https://i.postimg.cc/8z8cvk19/Frame-37242.png"
                                alt="Xpanse"
                                style={S.heroBandImg}
                            />
                        </div>

                        <div style={S.mainCard}>

                            {/* Report header */}
                            <div style={S.reportHeader}>
                                <div style={S.reportIcon}>📊</div>
                                <div>
                                    <h2 style={S.reportTitle}>Daily Summary Report</h2>
                                    <p style={S.reportSubtitle}>Outlet performance dashboard for the selected date</p>
                                </div>
                            </div>

                            {/* Meta: outlet + date */}
                            <div style={S.metaBox}>
                                <div style={S.metaCell}>
                                    <div style={S.metaLabel}>Outlet</div>
                                    <div style={S.metaValue}>{selectedOutletName || '—'}</div>
                                </div>
                                <div style={S.metaCellLast}>
                                    <div style={S.metaLabel}>Date</div>
                                    <div style={S.metaValue}>{formattedDate}</div>
                                </div>
                            </div>

                            {/* KPI Cards */}
                            <div style={S.kpiGrid}>
                                <KpiCard
                                    emoji="🧾" badge="ORDERS" badgeBg="#eaf8ef" badgeColor="#159947" borderColor="#d7efdf"
                                    label="Total Orders" value={tos.totalOrdersCount || 0} sub="Completed order volume"
                                    detail1L="With Coupons" detail1V={tos.totalCouponOrdersCount || 0} detail1Color="#dc2626"
                                    detail2L="Without Coupons" detail2V={tos.totalNonCouponOrdersCount || 0} detail2Color="#15803d"
                                />
                                <KpiCard
                                    emoji="💰" badge="NET" badgeBg="#eef4ff" badgeColor="#2563eb" borderColor="#d8e4ff"
                                    label="Revenue Before Tax" value={fmtINR(tos.totalRevenue)} sub="Net sales excluding tax"
                                    detail1L="Kiosk Revenue" detail1V={fmtINR(tos.totalKioskRevenue)} detail1Color="#2563eb"
                                    detail2L="Discount" detail2V={fmtINR(tos.totalCouponDiscount)} detail2Color="#ea580c"
                                />
                                <KpiCard
                                    emoji="📈" badge="FINAL" badgeBg="#ecfdf3" badgeColor="#15803d" borderColor="#d5f5e2"
                                    label="Final Revenue" value={fmtINR(grandTotal)} sub="Including tax"
                                    detail1L="Tax Collected" detail1V={fmtINR(tos.totaltotalTaxAmount)} detail1Color="#15803d"
                                    detail2L="Cancelled Value" detail2V={fmtINR(tos.totalCancelledOrdersRevenue)} detail2Color="#dc2626"
                                />
                                <KpiCard
                                    emoji="🛒" badge="AVG" badgeBg="#fff7ed" badgeColor="#ea580c" borderColor="#ffedd5"
                                    label="Avg Order Value" value={fmtINR(avgOrderValue)} sub="Revenue / orders"
                                    detail1L="App Orders" detail1V={appOrders} detail1Color="#2563eb"
                                    detail2L="Kiosk Orders" detail2V={tos.totalKioskOrdersCount || 0} detail2Color="#159947"
                                />
                            </div>

                            {/* Two-col panels */}
                            <div style={S.twoCol}>
                                <div style={S.panel}>
                                    <div style={S.panelTitle}>📱 Order Source Split</div>
                                    <div style={S.panelRowFirst}>
                                        <span>App Orders</span>
                                        <span>
                                            <strong style={{ marginRight: 12 }}>{appOrders}</strong>
                                            <strong style={{ color: '#2563eb' }}>{appPct}%</strong>
                                        </span>
                                    </div>
                                    <div style={S.panelRow}>
                                        <span>Kiosk Orders</span>
                                        <span>
                                            <strong style={{ marginRight: 12 }}>{tos.totalKioskOrdersCount || 0}</strong>
                                            <strong style={{ color: '#159947' }}>{kioskPct}%</strong>
                                        </span>
                                    </div>
                                </div>

                                <div style={S.panel}>
                                    <div style={S.panelTitle}>📦 Order Status Funnel</div>
                                    <div style={S.panelRowFirst}>
                                        <span>Total Orders</span>
                                        <strong>{tos.totalOrdersCount || 0}</strong>
                                    </div>
                                    <div style={S.panelRow}>
                                        <span>Completed Orders</span>
                                        <strong style={{ color: '#159947' }}>
                                            {(tos.totalOrdersCount || 0) - (tos.totalCancelledOrdersCount || 0)}
                                        </strong>
                                    </div>
                                    <div style={S.panelRow}>
                                        <span>Cancelled Orders</span>
                                        <strong style={{ color: '#dc2626' }}>{tos.totalCancelledOrdersCount || 0}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* Metrics table */}
                            <div style={S.tableWrap}>
                                <div style={S.tableHead}>
                                    <div style={S.thCell('center')}>#</div>
                                    <div style={S.thCell('left')}>Metric</div>
                                    <div style={S.thCell('right')}>Value</div>
                                </div>

                                <MetricRow idx={1} label="Orders Using Coupons" value={tos.totalCouponOrdersCount || 0} />
                                <MetricRow idx={2} label="Orders Without Coupons" value={tos.totalNonCouponOrdersCount || 0} bg="#fbfbfc" />
                                <MetricRow idx={3} label="App Orders" value={appOrders} valColor="#2563eb" />
                                <MetricRow idx={4} label="Kiosk Orders" value={tos.totalKioskOrdersCount || 0} bg="#fbfbfc" valColor="#159947" />
                                <MetricRow idx={5} label="Cancelled Orders" value={tos.totalCancelledOrdersCount || 0} valColor="#ff3b30" />
                                <MetricRow idx={6} label="Total Orders" value={tos.totalOrdersCount || 0} bg="#eefbf3" labelBold valColor="#159947" valBold />
                                <MetricRow idx={7} label="Average Order Value" value={fmtINR(avgOrderValue)} bg="#fff7ed" valColor="#ea580c" />
                                <MetricRow idx={8} label="Coupon Discount Given" value={fmtINR(tos.totalCouponDiscount)} valColor="#ea580c" />
                                <MetricRow idx={9} label="Kiosk Revenue" value={fmtINR(tos.totalKioskRevenue)} bg="#fbfbfc" />
                                <MetricRow idx={10} label="Revenue Before Tax" value={fmtINR(tos.totalRevenue)} bg="#eef4ff" labelBold valColor="#2563eb" valBold />
                                <MetricRow idx={11} label="Cancelled Orders Value" value={`- ${fmtINR(tos.totalCancelledOrdersRevenue)}`} valColor="#ff3b30" />
                                <MetricRow idx={12} label="Net Revenue (Excl. Tax)" value={fmtINR(netRevenue)} bg="#f0fdf4" labelBold valBold />
                                <MetricRow idx={13} label="Tax Collected" value={`+ ${fmtINR(tos.totaltotalTaxAmount)}`} bg="#fff1f2" valColor="#dc2626" />

                                {/* Grand Total */}
                                <div style={{ ...S.tableRow('#dcfce7'), borderTop: '1px solid #dbe9de' }}>
                                    <div style={{ ...S.tdNum, fontWeight: 800, fontSize: 16 }}>14</div>
                                    <div style={{ ...S.tdLabel(true), fontSize: 16, color: '#14532d' }}>Final Revenue (Including Tax)</div>
                                    <div style={{ ...S.tdVal('#15803d', true, 18) }}>{fmtINR(grandTotal)}</div>
                                </div>

                                {/* User info rows */}
                                <MetricRow idx={15} label="Users With Orders" value={`${userInfo?.totalUsersWithOrders || 0} nos`} />
                                <MetricRow idx={16} label="Referred Users With Orders" value={`${userInfo?.totalReferredUsersWithOrders || 0} nos`} bg="#fbfbfc" />
                                <MetricRow idx={17} label="Deleted Users With Orders" value={`${userInfo?.totalDeletedUsersWithOrders || 0} nos`} />
                                <MetricRow
                                    idx={18}
                                    label="Total Users Joined Today"
                                    note="(Count from all outlets)"
                                    value={`${userInfo?.totalUsersJoinedOnGivenDate || 0} nos`}
                                    bg="#eef4ff"
                                    labelBold
                                    valColor="#2563eb"
                                    valBold
                                />

                                {/* Coupon rows */}
                                {CouponCodeOrders?.map((coupon, i) => (
                                    <MetricRow
                                        key={i}
                                        idx={19 + i}
                                        label={<>COUPON — <span style={{ color: '#2563eb', fontWeight: 700 }}>{coupon?.coupon?.couponCode}</span></>}
                                        value={`${coupon?.count || 0} nos | ${fmtINR(coupon?.totalCouponDiscount)}`}
                                        bg={i % 2 === 0 ? '#fbfbfc' : '#fff'}
                                    />
                                ))}
                            </div>

                            {/* Revenue flow note */}
                            <div className='mb-4' style={S.noteBox}>
                                <strong style={{ color: '#1d4ed8' }}>Revenue Flow: </strong>
                                Revenue Before Tax → Net Revenue (Excl. Tax) → Tax Collected →{' '}
                                <strong style={{ color: '#15803d' }}>Final Revenue (Including Tax)</strong>
                            </div>

                        </div>{/* /mainCard */}

                        {/* Footer */}
                        <div style={S.footer}>
                            <p style={{ margin: 0 }}>
                                This is an automated daily summary report generated by the Xpanse reporting system.
                            </p>
                        </div>

                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

export default DaySummaryReport;